import type { Metric } from '@/payload/payload.types';
import { TargetValueIndicator } from '@tallinn-arvudes/ui/chart/utils';
import { type Where } from 'payload';

const normalizeValue = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const calculateMetricStatusInfo = ({
  realPeriods,
  tolerancePercentage,
  positiveDirection,
  targetLevel,
  targetPeriods,
  targetLevelDate,
}: Pick<
  Metric,
  | 'tolerancePercentage'
  | 'realPeriods'
  | 'positiveDirection'
  | 'targetPeriods'
  | 'targetLevel'
  | 'targetLevelDate'
>): Pick<Metric, 'trendArrowDirection' | 'trendArrowBackground'> => {
  let targetValue: string | number | null | undefined = targetLevel;
  if ((!targetValue || !targetLevelDate) && targetPeriods.length > 0) {
    const lastEl = targetPeriods[targetPeriods.length - 1];
    targetValue = lastEl.value;
  }

  if (
    targetValue === TargetValueIndicator.Increases ||
    targetValue === TargetValueIndicator.Decreases ||
    targetValue === TargetValueIndicator.SameLevel ||
    targetValue === TargetValueIndicator.DoesNotIncrease ||
    targetValue === TargetValueIndicator.DoesNotDecrease
  ) {
    targetValue = realPeriods[0]?.value ? Number(realPeriods[0].value) : null;
  } else if (targetLevel === TargetValueIndicator.NA) {
    targetValue = null;
  } else {
    const val = Number(targetValue);
    targetValue = targetValue == null || Number.isNaN(val) ? null : val;
  }

  const [prev, last] = realPeriods.slice(-2);

  let overrideArrowBackground: Metric['trendArrowBackground'] | undefined =
    undefined;

  const prevValue = normalizeValue(prev?.value);
  const lastValue = normalizeValue(last?.value);

  const compareValue = lastValue ?? prevValue ?? null;

  // If last real period meets the target value threshold, set the status background to green
  if (
    typeof compareValue === 'number' &&
    typeof targetValue === 'number' &&
    Number.isFinite(targetValue)
  ) {
    if (
      (positiveDirection === 'bigger_better' && compareValue >= targetValue) ||
      (positiveDirection === 'smaller_better' && compareValue <= targetValue)
    ) {
      overrideArrowBackground = 'green';
    }
  }

  if (realPeriods.length < 2 || lastValue === null || prevValue === null) {
    return {
      trendArrowDirection: 'no_data',
      trendArrowBackground: 'no_data',
    };
  }

  const diff = lastValue - prevValue;
  const tolerance =
    typeof tolerancePercentage === 'number' &&
    Number.isFinite(tolerancePercentage)
      ? tolerancePercentage
      : 0;

  const percentChange =
    prevValue === 0
      ? diff === 0
        ? 0
        : diff > 0
          ? Number.POSITIVE_INFINITY
          : Number.NEGATIVE_INFINITY
      : (diff / prevValue) * 100;

  if (Number.isFinite(percentChange) && Math.abs(percentChange) <= tolerance) {
    return {
      trendArrowDirection: 'same',
      trendArrowBackground: overrideArrowBackground ?? 'yellow',
    };
  }

  if (diff < 0) {
    return {
      trendArrowDirection: 'down',
      trendArrowBackground:
        overrideArrowBackground ??
        (positiveDirection === 'bigger_better' ? 'red' : 'green'),
    };
  }

  return {
    trendArrowDirection: 'up',
    trendArrowBackground:
      overrideArrowBackground ??
      (positiveDirection === 'bigger_better' ? 'green' : 'red'),
  };
};

export const whereMetricActive = (where?: Where | undefined): Where => {
  return {
    ...where,
    and: [
      {
        activeStatus: {
          equals: 'active',
        },
        isValid: {
          equals: true,
        },
        _status: {
          equals: 'published',
        },
      },
    ],
  };
};

export const shouldShowMetric = <
  T extends {
    isValid: boolean;
    activeStatus: 'active' | 'inactive';
    _status?: 'published' | 'draft' | null;
  },
>(
  data: T,
  showDrafts: boolean = false,
): boolean => {
  if (showDrafts) {
    return data.activeStatus === 'active' && data.isValid;
  }
  return (
    data._status === 'published' &&
    data.activeStatus === 'active' &&
    data.isValid
  );
};
